import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function ServicesPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: 'ri-test-tube-line',
      title: 'Lead Inspections & Risk Assessments',
      description: 'EPA-certified lead inspections and risk assessments for homes, daycares, and commercial properties. We identify lead-based paint hazards, evaluate risks, and provide clear reports that meet federal and state requirements.',
      details: [
        'Visual inspection of painted surfaces',
        'XRF testing for lead content',
        'Dust wipe sampling',
        'Risk assessment reports',
        'Compliance documentation',
      ],
      link: '/lead-inspections',
    },
    {
      icon: 'ri-building-line',
      title: 'Asbestos Surveys & Consulting',
      description: 'Professional asbestos surveys for renovation and demolition projects. We identify asbestos-containing materials, assess condition, and develop management plans that keep your project on track and compliant.',
      details: [
        'Pre-renovation surveys',
        'Demolition assessments',
        'Bulk sample analysis',
        'Management plans',
        'Regulatory compliance',
      ],
      link: '/asbestos-surveys',
    },
    {
      icon: 'ri-lungs-line',
      title: 'Indoor Air Quality & Mold',
      description: 'Comprehensive indoor air quality testing and mold investigations. We find the source of the problem, test what matters, and give you a clear path to fix it-whether it\'s a single building or an entire portfolio.',
      details: [
        'Air quality testing',
        'Mold inspection & sampling',
        'Moisture assessment',
        'HVAC evaluation',
        'Remediation protocols',
      ],
      link: '/indoor-air-quality',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Abatement Design & Management',
      description: 'When hazards are found, we design the fix and manage the process. LCA prepares abatement plans and specifications, assists with contractor selection and bidding, and provides third-party oversight and clearance testing so projects are safe, compliant, and cost-effective.',
      details: [
        'Project specifications',
        'Contractor bid review',
        'On-site monitoring',
        'Clearance testing',
        'Final documentation',
      ],
      link: '/abatement-design',
    },
  ];

  const technicalCapabilities = [
    {
      title: 'Hazardous Building Materials Programs',
      description: 'Comprehensive asbestos, lead-based paint, PCB, and universal waste surveys; Operations & Maintenance (O&M) programs; abatement design; third-party air monitoring.',
    },
    {
      title: 'Environmental Due Diligence',
      description: 'Phase I, II, and III environmental site assessments, Environmental Condition of Property (ECP) reports, property condition assessments, and hazardous materials assessments for acquisitions, lending, and redevelopment.',
    },
    {
      title: 'Indoor Air Quality & Microbial Services',
      description: 'IAQ investigations using state-of-the-art sampling, mold and microbial assessments, remediation plans, and prevention / response programs for schools, healthcare, and commercial portfolios.',
    },
    {
      title: 'Portfolio & Program Management',
      description: 'Multi-site environmental programs for military housing, agencies, and large private owners, with standardized protocols, web-based data, and ongoing regulatory compliance support.',
    },
    {
      title: 'Specialty Testing & Support',
      description: 'Lead-in-drinking-water investigations, radon sampling, universal waste and PCB management, and regulatory strategy for complex facilities.',
    },
  ];

  const whyChoose = [
    {
      icon: 'ri-building-4-line',
      title: 'Deep experience with complex, multi-site portfolios',
    },
    {
      icon: 'ri-team-line',
      title: 'Senior professionals directly involved in every project',
    },
    {
      icon: 'ri-compass-3-line',
      title: 'Clear, practical guidance from survey through abatement and closure',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our Services
          </h1>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Comprehensive environmental consulting to keep homes, housing communities, daycares, and commercial properties safe, compliant, and healthy.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10">
            {services.map((service, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-10 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-[#f59e0b]"
              >
                <div className="flex items-start mb-6">
                  <div className="w-16 h-16 bg-gray-50 text-black rounded-xl flex items-center justify-center mr-5 flex-shrink-0 group-hover:bg-[#f59e0b] group-hover:text-white transition-all duration-300">
                    <i className={`${service.icon} text-3xl`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-black mb-3">{service.title}</h3>
                  </div>
                </div>
                <p className="text-base text-gray-600 leading-relaxed mb-6">{service.description}</p>
                
                <div className="space-y-3 mb-6">
                  {service.details.map((detail, idx) => (
                    <div key={idx} className="flex items-center">
                      <i className="ri-checkbox-circle-fill text-[#f59e0b] mr-3 text-lg flex-shrink-0"></i>
                      <span className="text-gray-700 text-sm">{detail}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to={service.link}
                  className="text-[#f59e0b] hover:text-[#d97706] font-semibold transition-colors cursor-pointer inline-flex items-center group"
                >
                  Learn more 
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Capabilities */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Specialized Programs & Technical Capabilities
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              For complex projects and large portfolios, LCA provides a full suite of technical services and program-level support.
            </p>
          </div>

          <div className="space-y-6 max-w-5xl mx-auto">
            {technicalCapabilities.map((capability, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 hover:shadow-lg transition-shadow duration-300"
              >
                <h3 className="text-xl font-bold text-black mb-3">{capability.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose LCA */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">Why Clients Choose LCA</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {whyChoose.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 text-center border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300"
              >
                <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                  <i className={`${item.icon} text-3xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-black leading-relaxed">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#f59e0b] to-[#d97706] rounded-3xl p-12 text-center shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6">
              Not sure which service you need?
            </h2>
            <p className="text-xl text-white/95 mb-8 leading-relaxed">
              Tell us about your property or portfolio, and we'll point you to the right starting point—no jargon, no pressure.
            </p>
            <Link
              to="/contact#contact-form"
              className="inline-block bg-white text-[#f59e0b] px-10 py-4 rounded-lg text-base font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer"
            >
              Talk to an expert
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
